/** Prometheus-style label set; string keys map to string label values. */
export type ExampleMetric = Record<string, string>;

export type Datapoint = [number, string];

export type SeriesData = {
  metric: ExampleMetric;
  values: Datapoint[];
};

export type VectorSeriesData = {
  metric: ExampleMetric;
  value: Datapoint;
};

export type MockResponse = {
  status: string;
  data: Record<string, unknown>;
};

export type MockConstantValueResultConfig = {
  metric: Record<string, string>;
  value: string;
};

export interface MockTimeSeriesValueConfig {
  startTimeMs?: number;
  endTimeMs?: number;
  totalDatapoints?: number;
  queryType?: 'Instant' | 'Range';
  stepMs?: number;
}

export interface MockConstantValueConfig extends MockTimeSeriesValueConfig {
  metrics: MockConstantValueResultConfig[];
}

export interface MockManySeriesConfig extends MockTimeSeriesValueConfig {
  totalSeries?: number;
  /**
   * When true, the results will not have labels. Otherwise, a default set of labels will be populated.
   */
  noLabels?: boolean;
}

export interface MockInstantManySeriesConfig {
  totalSeries?: number;
  /** Evaluation time in milliseconds; converted to Unix seconds for the vector sample. Defaults to MOCK_END_TIME. */
  endTimeMs?: number;
  /** Value range each sample is drawn from (uniform spread). Defaults to [0, 100]. */
  valueRange?: [min: number, max: number];
  /** Label key used to distinguish series (e.g. "cluster", "instance"). Defaults to "job". */
  labelKey?: string;
  /** Metric `__name__`. Defaults to "mock_metric". */
  metricName?: string;
}

export interface RandomNumberGenerator {
  nextInt: () => number;
  nextFloat: () => number;
  nextRange: (start: number, end: number) => number;
}

export interface MockRealisticSeriesConfig extends Omit<MockManySeriesConfig, 'noLabels'> {
  /**
   * Used so generated data is deterministic for visual regression tests.
   * Adjusting seed will change series names and the shape of the data.
   */
  seed?: number;
  /**
   * Multiplier for the y-axis variation to simulate seasonality.
   */
  yVariationMultiplier?: number;
  /**
   * Ratio of the series that will contain outliers. Value should be between 0 and 1.
   */
  outlierRatio?: number;
  /**
   * Controls magnitude of outliers to exaggerate their impact.
   */
  outlierMultiplier?: number;
  /**
   * Increase to simulate natural variability.
   * Raising above 1000 gives best results.
   */
  randomnessMultiplier?: number;
  /**
   * Ratio of sparse datapoints, value should be between 0 (no data) and 1 (no sparsity).
   * Between 0.01 (extremely sparse) and 0.05 (somewhat sparse) give best results.
   */
  sparseRatio?: number;
}

export type MockTimeSeriesResponseWithRealisticSeriesArgs = MockRealisticSeriesConfig & {
  outlierRatio?: number;
  outlierMultiplier?: number;
};

// Mock time range values used for mocking the time range in the system and
// mock data responses to ensure consistent results when viewing and taking
// visual testing snapshots of stories.
// Currenting has a 1 hour time range. Intentionally using a smaller time range
// to avoid the overhead associated with this when taking screenshots in happo
// with really large data series.
export const TIMESERIES_EXAMPLE_MOCK_END = 1673805600000;
export const TIMESERIES_EXAMPLE_MOCK_START = TIMESERIES_EXAMPLE_MOCK_END - 1 * 60 * 60 * 1000;

// TODO: Use these values instead of the values above as we introduce defaults to the mocking functions.
// Eventually, callers won't have to specify startTime or endTime in most cases.
export const MOCK_END_TIME = 1673805600000;
export const MOCK_START_TIME_ONE_HOUR_EARLIER = MOCK_END_TIME - 1 * 60 * 60 * 1000;
export const MOCK_START_TIME_SIX_HOURS_EARLIER = MOCK_END_TIME - 6 * 60 * 60 * 1000;
export const MOCK_START_TIME = MOCK_START_TIME_SIX_HOURS_EARLIER;
